import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { fmtDate } from "@/lib/format";
import { Mail, MessageSquare, CheckCircle2, XCircle, Send, History } from "lucide-react";

function StatusBadge({ ok, live, source }) {
  if (live && ok) return <Badge variant="outline" className="text-success border-success/40 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1 inline" />Delivered ({source})</Badge>;
  if (!live) return <Badge variant="outline" className="text-warning border-warning/40 text-[10px]">Not Sent</Badge>;
  return <Badge variant="outline" className="text-destructive border-destructive/40 text-[10px]"><XCircle className="h-3 w-3 mr-1 inline" />Failed</Badge>;
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const canManage = user?.role === "HO" || user?.role === "SUPER_ADMIN" || user?.role === "RO";
  const [health, setHealth] = useState(null);
  const [list, setList] = useState([]);
  const [email, setEmail] = useState({ to: "", subject: "Test from ACAS", body_html: "<p>Hello — this is a test notification from your bank.</p>" });
  const [sms, setSms] = useState({ to: "", body: "Traceroot: test SMS from your bank." });
  const [busyE, setBusyE] = useState(false);
  const [busyS, setBusyS] = useState(false);

  const load = async () => {
    try {
      const [h, n] = await Promise.all([
        api.get("/notifications/health"),
        api.get("/notifications?limit=50"),
      ]);
      setHealth(h.data); setList(n.data);
    } catch (e) {}
  };
  useEffect(() => { if (canManage) load(); /* eslint-disable-next-line */ }, [canManage]);

  const sendEmail = async () => {
    if (!email.to || !email.subject) return toast.error("To address and subject required");
    setBusyE(true);
    try {
      const r = await api.post("/notifications/send-email", email);
      if (r.data.delivered) toast.success("Email delivered");
      else toast.warning(r.data.error || "Email not delivered (no provider configured)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusyE(false);
  };

  const sendSms = async () => {
    if (!sms.to || !sms.body) return toast.error("To number and body required");
    setBusyS(true);
    try {
      const r = await api.post("/notifications/send-sms", sms);
      if (r.data.delivered) toast.success("SMS delivered");
      else toast.warning(r.data.error || "SMS not delivered (no provider configured)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusyS(false);
  };

  if (!canManage) {
    return <Card className="p-8 text-center text-muted-foreground">Only HO / RO / SUPER_ADMIN can view notifications.</Card>;
  }

  return (
    <div className="space-y-5">
      <SectionHeader title="Notifications" subtitle="Email & SMS dispatch log with provider health and test sending." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2"><Mail className="h-4 w-4 text-accent" /><span className="font-semibold heading text-sm">Email Provider</span></div>
          {health?.email?.enabled ? (
            <div className="space-y-1">
              <Badge variant="outline" className="text-success border-success/40">
                <CheckCircle2 className="h-3 w-3 mr-1 inline" />{health.email.provider_id} configured ({health.email.source}, {health.email.sandbox ? "sandbox" : "live"})
              </Badge>
              <p className="text-xs text-muted-foreground">Outbound email is wired to {health.email.provider_id}.</p>
            </div>
          ) : (
            <div className="space-y-1">
              <Badge variant="outline" className="text-warning border-warning/40">No provider configured</Badge>
              <p className="text-xs text-muted-foreground">Set <code className="mono text-[11px]">SENDGRID_API_KEY</code> env var, or configure <em>email_provider</em> on this bank in Super Admin → Bank → External APIs.</p>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2"><MessageSquare className="h-4 w-4 text-accent" /><span className="font-semibold heading text-sm">SMS Provider</span></div>
          {health?.sms?.enabled ? (
            <div className="space-y-1">
              <Badge variant="outline" className="text-success border-success/40">
                <CheckCircle2 className="h-3 w-3 mr-1 inline" />{health.sms.provider_id} configured ({health.sms.source})
              </Badge>
              <p className="text-xs text-muted-foreground">Outbound SMS is wired to {health.sms.provider_id}.</p>
            </div>
          ) : (
            <div className="space-y-1">
              <Badge variant="outline" className="text-warning border-warning/40">No provider configured</Badge>
              <p className="text-xs text-muted-foreground">Set <code className="mono text-[11px]">TWILIO_ACCOUNT_SID</code> + <code className="mono text-[11px]">TWILIO_AUTH_TOKEN</code> + <code className="mono text-[11px]">TWILIO_FROM_NUMBER</code> env vars, or configure <em>sms_provider</em> on this bank.</p>
            </div>
          )}
        </Card>
      </div>

      <Tabs defaultValue="send" className="w-full">
        <TabsList>
          <TabsTrigger value="send" data-testid="notif-tab-send"><Send className="h-3.5 w-3.5 mr-1" />Send Test</TabsTrigger>
          <TabsTrigger value="log" data-testid="notif-tab-log"><History className="h-3.5 w-3.5 mr-1" />Recent ({list.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="send">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 space-y-3">
              <div className="font-semibold heading text-sm flex items-center gap-2"><Mail className="h-4 w-4" /> Test Email</div>
              <div className="space-y-1.5"><Label>To</Label><Input value={email.to} onChange={(e) => setEmail({...email, to: e.target.value})} placeholder="customer@example.com" data-testid="notif-email-to" /></div>
              <div className="space-y-1.5"><Label>Subject</Label><Input value={email.subject} onChange={(e) => setEmail({...email, subject: e.target.value})} /></div>
              <div className="space-y-1.5"><Label>Body (HTML)</Label><Textarea value={email.body_html} onChange={(e) => setEmail({...email, body_html: e.target.value})} rows={4} /></div>
              <Button onClick={sendEmail} disabled={busyE} className="w-full" data-testid="notif-send-email">{busyE ? "Sending…" : "Send Email"}</Button>
            </Card>
            <Card className="p-4 space-y-3">
              <div className="font-semibold heading text-sm flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Test SMS</div>
              <div className="space-y-1.5"><Label>To (E.164 format)</Label><Input value={sms.to} onChange={(e) => setSms({...sms, to: e.target.value})} placeholder="+919876543210" data-testid="notif-sms-to" /></div>
              <div className="space-y-1.5"><Label>Body</Label><Textarea value={sms.body} onChange={(e) => setSms({...sms, body: e.target.value})} rows={4} maxLength={160} /></div>
              <Button onClick={sendSms} disabled={busyS} className="w-full" data-testid="notif-send-sms">{busyS ? "Sending…" : "Send SMS"}</Button>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="log">
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="text-left px-3 py-2.5">When</th><th className="text-left px-3 py-2.5">Channel</th><th className="text-left px-3 py-2.5">To</th><th className="text-left px-3 py-2.5">Subject / Body</th><th className="text-left px-3 py-2.5">Provider</th><th className="text-left px-3 py-2.5">Status</th></tr>
              </thead>
              <tbody>
                {list.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No notifications yet.</td></tr>}
                {list.map((n) => (
                  <tr key={n.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 mono text-xs whitespace-nowrap">{fmtDate(n.sent_at)}</td>
                    <td className="px-3 py-2"><Badge variant="secondary" className="text-[10px]">{n.channel.toUpperCase()}</Badge></td>
                    <td className="px-3 py-2 mono text-xs">{n.to}</td>
                    <td className="px-3 py-2 text-xs max-w-md truncate">{n.subject ? <span className="font-medium">{n.subject}</span> : null}<div className="text-muted-foreground truncate">{n.body_preview}</div>{n.event && <Badge variant="outline" className="text-[10px] mt-1">{n.event}</Badge>}</td>
                    <td className="px-3 py-2 text-xs">{n.provider_id || "—"}<div className="text-muted-foreground text-[10px]">{n.provider_source || ""}</div></td>
                    <td className="px-3 py-2"><StatusBadge ok={n.ok} live={n.live} source={n.provider_source} />{n.error && <div className="text-[10px] text-destructive mt-1 max-w-xs truncate" title={n.error}>{n.error}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
